import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calculator, BarChart3, FileText, LineChart, SlidersHorizontal, PiggyBank, Building2, Users, BookOpen, ArrowLeft, ExternalLink } from "lucide-react";
import { Button, Input, Stat } from "../components/ui";
import { calculateObjectCockpit, getObjectCockpitDefaults } from "../lib/objectCockpit";
import { useI18n } from "../i18n/I18nContext";
import { useRateDefault, rateHint } from "../lib/useRateDefault";
import { markOnboardingTask } from "../lib/onboarding";

const TABS = [
  { key: "cockpit", label: "Cockpit", icon: Calculator },
  { key: "bank", label: "Bankgespräch", icon: FileText },
  { key: "history", label: "Kennzahlen im Verlauf", icon: LineChart },
  { key: "charts", label: "Diagramme", icon: BarChart3 },
  { key: "config", label: "Konfiguration", icon: SlidersHorizontal },
  { key: "household", label: "Haushaltsrechnung", icon: PiggyBank },
  { key: "assets", label: "Vermögensaufstellung", icon: Building2 },
];

const STORAGE_KEY = "immo_object_cockpit_form";

const DE_STATES = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg", "Hessen",
  "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen", "Rheinland-Pfalz",
  "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen",
];
const AT_STATES = ["Burgenland", "Kärnten", "Niederösterreich", "Oberösterreich", "Salzburg", "Steiermark", "Tirol", "Vorarlberg", "Wien"];

const DEFAULT_HOUSEHOLD = {
  netIncomeMonthly: 4200,
  otherIncomeMonthly: 250,
  fixedCostsMonthly: 1800,
  existingDebtServiceMonthly: 0,
};

const DEFAULT_ASSETS = {
  cash: 25000,
  securities: 15000,
  otherAssets: 0,
  existingDebt: 0,
};

export default function ObjectCockpit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState("cockpit");
  const [form, setForm] = useState(() => loadStoredForm());
  const rateDefault = useRateDefault(getObjectCockpitDefaults().rate);

  useEffect(() => { markOnboardingTask("cockpit"); }, []);

  // Seed the market rate ONLY while the field still holds the built-in default:
  // the form is persisted to localStorage, and a rate the user typed themselves
  // must never be overwritten by a later /rates fetch.
  useEffect(() => {
    if (rateDefault.source !== "rates") return;
    setForm((prev) => (prev.rate === getObjectCockpitDefaults().rate
      ? { ...prev, rate: rateDefault.rate }
      : prev));
  }, [rateDefault.source, rateDefault.rate]);
  const [household, setHousehold] = useState(DEFAULT_HOUSEHOLD);
  const [assets, setAssets] = useState(DEFAULT_ASSETS);
  const [listingCtx, setListingCtx] = useState(null);

  // Incoming prefill (from a listing profile) wins over the persisted form —
  // the user explicitly opened a fresh object. Applied once per nav state so
  // the form stays editable afterwards; the persistence effect below then
  // naturally stores the prefilled form.
  const appliedPrefillRef = useRef(null);
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill || appliedPrefillRef.current === prefill) return;
    appliedPrefillRef.current = prefill;
    if (prefill.form) setForm((prev) => ({ ...prev, ...prefill.form }));
    setListingCtx({
      listingId: prefill.listingId || null,
      listingUrl: prefill.listingUrl || "",
      backTo: prefill.backTo || (prefill.listingId ? `/listings/${prefill.listingId}` : null),
    });
  }, [location.state]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
  }, [form]);

  const result = useMemo(() => calculateObjectCockpit(form), [form]);
  const title = form.title?.trim() || "Objekt-Cockpit";
  const annualTaxRate = `${Math.round((form.taxRate || 0) * 100)}%`;
  const householdBeforeNewDebt = household.netIncomeMonthly + household.otherIncomeMonthly - household.fixedCostsMonthly - household.existingDebtServiceMonthly;
  const householdAfterNewDebt = householdBeforeNewDebt - result.mortgageMonthly;
  const netWorth = assets.cash + assets.securities + assets.otherAssets - assets.existingDebt + result.equityRequired;
  const purchaseTotal = numField(result.inputs.purchasePrice) + result.acquisitionCosts + result.upfrontRenovation;

  return (
    <div className="reveal">
      <header className="border-b border-border px-6 md:px-10 py-6 bg-grid">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            {(listingCtx?.backTo || listingCtx?.listingUrl) && (
              <div className="flex flex-wrap items-center gap-4 mb-3">
                {listingCtx?.backTo && (
                  <button
                    onClick={() => navigate(listingCtx.backTo)}
                    className="overline hover:text-white inline-flex items-center gap-2"
                    data-testid="cockpit-back-to-listing"
                  >
                    <ArrowLeft size={12} /> {t("common.backToListing")}
                  </button>
                )}
                {listingCtx?.listingUrl && (
                  <a
                    href={listingCtx.listingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="overline hover:text-white inline-flex items-center gap-2"
                    data-testid="cockpit-listing-url"
                  >
                    <ExternalLink size={12} /> {t("common.openListing")}
                  </a>
                )}
              </div>
            )}
            <div className="overline">Objektanalyse</div>
            <h1 className="mt-1 font-heading text-3xl md:text-4xl font-black tracking-tight">{title}</h1>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px border border-border bg-border w-full lg:w-[720px]">
            <Stat label="Bruttorendite" value={`${result.grossYieldPct.toFixed(2)}%`} />
            <Stat label="Faktor" value={result.factor ? result.factor.toFixed(2) : "—"} />
            <Stat label="Cashflow / Monat" value={fmtMoney(result.cashflowAfterTaxMonthly)} accent={result.cashflowAfterTaxMonthly >= 0 ? "green" : "red"} />
            <Stat label="Volltilgung" value={result.debtFreeYear || "—"} sub="Jahr" />
          </div>
        </div>
      </header>

      <div className="grid grid-cols-12">
        <aside className="col-span-12 lg:col-span-4 border-r border-border p-6 space-y-6">
          <SectionTitle icon={BookOpen} title="Stammdaten" />
          <div className="space-y-3">
            <Field label="Objekttitel">
              <Input value={form.title || ""} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="Objekt-Cockpit" />
            </Field>
            <Field label="Adresse">
              <Input value={form.address || ""} onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))} placeholder="Musterstraße 11, 12345 Musterstadt" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Land">
                <select
                  value={form.country || "AT"}
                  onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value, state: "" }))}
                  className="w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white"
                >
                  <option value="DE">DE</option>
                  <option value="AT">AT</option>
                </select>
              </Field>
              <Field label="Bundesland">
                <select
                  value={form.state || ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value }))}
                  className="w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white"
                >
                  <option value="">Auswählen</option>
                  {(form.country === "AT" ? AT_STATES : DE_STATES).map((state) => <option key={state} value={state}>{state}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Kaufdatum">
              <Input type="date" value={form.purchaseDate || ""} onChange={(e) => setForm((prev) => ({ ...prev, purchaseDate: e.target.value }))} />
            </Field>
          </div>

          <SectionTitle icon={Building2} title="Objekt" />
          <div className="space-y-3">
            <Grid2>
              <Field label="Kaufpreis">
                <Input type="number" value={form.purchasePrice} onChange={(e) => setNumField(setForm, "purchasePrice", e.target.value)} />
              </Field>
              <Field label="Wohnfläche m²">
                <Input type="number" value={form.livingAreaSqm} onChange={(e) => setNumField(setForm, "livingAreaSqm", e.target.value)} />
              </Field>
              <Field label="Kaltmiete €/m²">
                <Input type="number" value={form.coldRentPerSqmMonthly} onChange={(e) => setNumField(setForm, "coldRentPerSqmMonthly", e.target.value)} />
              </Field>
              <Field label="Stellplätze">
                <Input type="number" value={form.parkingSpaces} onChange={(e) => setNumField(setForm, "parkingSpaces", e.target.value)} />
              </Field>
              <Field label="Stellplatz €/Monat">
                <Input type="number" value={form.parkingRentMonthly} onChange={(e) => setNumField(setForm, "parkingRentMonthly", e.target.value)} />
              </Field>
              <Field label="Sonstige Miete €/Monat">
                <Input type="number" value={form.otherMonthlyRent} onChange={(e) => setNumField(setForm, "otherMonthlyRent", e.target.value)} />
              </Field>
            </Grid2>
          </div>

          <SectionTitle icon={SlidersHorizontal} title="Kauf & Finanzierung" />
          <div className="space-y-3">
            <Grid2>
              <Field label="Makler">
                <PercentInput value={form.brokerPct} onChange={(v) => setForm((prev) => ({ ...prev, brokerPct: v }))} />
              </Field>
              <Field label="Notar">
                <PercentInput value={form.notaryPct} onChange={(v) => setForm((prev) => ({ ...prev, notaryPct: v }))} />
              </Field>
              <Field label="Grundbuch">
                <PercentInput value={form.landRegistryPct} onChange={(v) => setForm((prev) => ({ ...prev, landRegistryPct: v }))} />
              </Field>
              <Field label="GrESt">
                <PercentInput value={form.transferTaxPct ?? 0} onChange={(v) => setForm((prev) => ({ ...prev, transferTaxPct: v }))} />
              </Field>
              <Field label="Sonst. NK">
                <PercentInput value={form.otherAcquisitionPct} onChange={(v) => setForm((prev) => ({ ...prev, otherAcquisitionPct: v }))} />
              </Field>
              <Field label="Sondermaßnahmen">
                <Input type="number" value={form.upfrontRenovation} onChange={(e) => setNumField(setForm, "upfrontRenovation", e.target.value)} />
              </Field>
              <Field label="LTV">
                <PercentInput value={form.ltv} onChange={(v) => setForm((prev) => ({ ...prev, ltv: v }))} />
              </Field>
              <Field label="Zins" hint={rateHint(rateDefault, t)}>
                <PercentInput value={form.rate} onChange={(v) => setForm((prev) => ({ ...prev, rate: v }))} />
              </Field>
              <Field label="Anfängliche Tilgung">
                <PercentInput value={form.initialRepaymentPct} onChange={(v) => setForm((prev) => ({ ...prev, initialRepaymentPct: v }))} />
              </Field>
              <Field label="Zinsbindung (Jahre)">
                <Input type="number" value={form.interestFixationYears} onChange={(e) => setNumField(setForm, "interestFixationYears", e.target.value)} />
              </Field>
              <Field label="Beleihungswert, optional">
                <Input type="number" value={form.collateralValue ?? ""} onChange={(e) => setNumField(setForm, "collateralValue", e.target.value)} />
              </Field>
              <Field label="Steuersatz">
                <PercentInput value={form.taxRate} onChange={(v) => setForm((prev) => ({ ...prev, taxRate: v }))} />
              </Field>
            </Grid2>
          </div>

          <SectionTitle icon={Users} title="Bewirtschaftung" />
          <div className="space-y-3">
            <Grid2>
              <Field label="Hausgeld umlagefähig">
                <Input type="number" value={form.nonAllocableHouseMoneyMonthly} onChange={(e) => setNumField(setForm, "nonAllocableHouseMoneyMonthly", e.target.value)} />
              </Field>
              <Field label="Verwaltung / Monat">
                <Input type="number" value={form.managementMonthly} onChange={(e) => setNumField(setForm, "managementMonthly", e.target.value)} />
              </Field>
              <Field label="Leerstand">
                <PercentInput value={form.vacancyPct} onChange={(v) => setForm((prev) => ({ ...prev, vacancyPct: v }))} />
              </Field>
              <Field label="Instandhaltung">
                <PercentInput value={form.maintenancePct} onChange={(v) => setForm((prev) => ({ ...prev, maintenancePct: v }))} />
              </Field>
              <Field label="Gebäudeanteil">
                <PercentInput value={form.buildingSharePct} onChange={(v) => setForm((prev) => ({ ...prev, buildingSharePct: v }))} />
              </Field>
              <Field label="Baujahr">
                <Input type="number" value={form.baujahr} onChange={(e) => setNumField(setForm, "baujahr", e.target.value)} />
              </Field>
            </Grid2>
          </div>

          <SectionTitle icon={LineChart} title="Prognose" />
          <div className="space-y-3">
            <Grid2>
              <Field label="Mietsteigerung">
                <PercentInput value={form.annualRentGrowthPct} onChange={(v) => setForm((prev) => ({ ...prev, annualRentGrowthPct: v }))} />
              </Field>
              <Field label="Wertsteigerung">
                <PercentInput value={form.annualValueGrowthPct} onChange={(v) => setForm((prev) => ({ ...prev, annualValueGrowthPct: v }))} />
              </Field>
              <Field label="Kostensteigerung">
                <PercentInput value={form.annualCostGrowthPct} onChange={(v) => setForm((prev) => ({ ...prev, annualCostGrowthPct: v }))} />
              </Field>
              <Field label="Prognosejahre">
                <Input type="number" value={form.projectionYears} onChange={(e) => setNumField(setForm, "projectionYears", e.target.value)} />
              </Field>
            </Grid2>
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-8">
          <div className="border-b border-border px-6 py-4 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest2 border ${
                      active ? "border-signal-green text-white bg-signal-green/10" : "border-border text-text-muted hover:text-white hover:border-white/40"
                    }`}
                  >
                    <Icon size={12} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6 space-y-6">
            {activeTab === "cockpit" && (
              <>
                <section className="border border-border bg-surface p-5">
                  <div className="overline">Cockpit</div>
                  <div className="mt-2 text-3xl md:text-5xl font-light text-white num">
                    {fmtMoney(result.cashflowAfterTaxMonthly)}
                  </div>
                  <div className="mt-2 text-xs font-mono text-text-muted">
                    Cashflow nach Steuern pro Monat · Steuersatz {annualTaxRate}
                  </div>
                </section>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px border border-border bg-border">
                  <Stat label="Gesamtinvestition" value={fmtMoney(purchaseTotal)} />
                  <Stat label="Eigenkapitalbedarf" value={fmtMoney(result.equityRequired)} accent="yellow" />
                  <Stat label="Bruttomiete / Jahr" value={fmtMoney(result.annualNetRent)} />
                  <Stat label="Nettomiete / Monat" value={fmtMoney(result.monthlyColdRent)} />
                  <Stat label="Cashflow vor Steuern" value={fmtMoney(result.cashflowBeforeTaxMonthly)} accent={result.cashflowBeforeTaxMonthly >= 0 ? "green" : "red"} />
                  <Stat label="Cashflow nach Steuern" value={fmtMoney(result.cashflowAfterTaxMonthly)} accent={result.cashflowAfterTaxMonthly >= 0 ? "green" : "red"} />
                  <Stat label="Bruttorendite" value={`${result.grossYieldPct.toFixed(2)}%`} />
                  <Stat label="Netto-Rendite" value={`${result.netYieldPct.toFixed(2)}%`} />
                </div>
                <section className="border border-border p-5">
                  <div className="overline mb-3">Kurzfazit</div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <p className="text-text-secondary leading-relaxed">
                      Monatliche Warmmiete: <span className="text-white num">{fmtMoney(result.monthlyWarmRent)}</span>.
                      Monatliche Annuität: <span className="text-white num">{fmtMoney(result.mortgageMonthly)}</span>.
                    </p>
                    <p className="text-text-secondary leading-relaxed">
                      Geschätzte Abschreibung p.a.: <span className="text-white num">{fmtMoney(result.depreciationAnnual)}</span>.
                      Schuldfrei laut Modell: <span className="text-white num">{result.debtFreeYear || "—"}</span>.
                    </p>
                  </div>
                </section>
              </>
            )}

            {activeTab === "bank" && (
              <section className="border border-border p-5">
                <div className="overline mb-3">Bankgespräch</div>
                <div className="grid lg:grid-cols-2 gap-6">
                  <SummaryTable
                    title="Investition"
                    rows={[
                      ["Kaufpreis", fmtMoney(form.purchasePrice)],
                      ["Kaufnebenkosten", fmtMoney(result.acquisitionCosts)],
                      ["Sondermaßnahmen", fmtMoney(result.upfrontRenovation)],
                      ["Eigenkapitalbedarf", fmtMoney(result.equityRequired)],
                    ]}
                  />
                  <SummaryTable
                    title="Ertrag"
                    rows={[
                      ["Jahresmiete", fmtMoney(result.annualNetRent)],
                      ["Rendite brutto", `${result.grossYieldPct.toFixed(2)}%`],
                      ["Rendite netto", `${result.netYieldPct.toFixed(2)}%`],
                      ["Cash-on-Cash", `${result.cashOnCashPct.toFixed(2)}%`],
                    ]}
                  />
                </div>
                <div className="mt-6 border-t border-border pt-4">
                  <Button
                    variant="green"
                    onClick={() => navigate("/finanzierungsdossier", { state: { cockpit: { form, household, assets } } })}
                    data-testid="open-financing-dossier"
                  >
                    <FileText size={13} /> Finanzierungsdossier erstellen
                  </Button>
                </div>
              </section>
            )}

            {activeTab === "history" && (
              <section className="border border-border p-5">
                <div className="overline mb-3">Kennzahlen im Verlauf</div>
                <HistoryTable rows={result.timeline} />
              </section>
            )}

            {activeTab === "charts" && (
              <section className="space-y-5">
                <ChartBand title="Miete und Wert" leftLabel="Miete" rightLabel="Wert" pointsA={result.timeline.map((r) => r.annualNetRent)} pointsB={result.timeline.map((r) => r.propertyValue)} />
                <ChartBand title="Schuld und Eigenkapital" leftLabel="Restschuld" rightLabel="Eigenkapital" pointsA={result.timeline.map((r) => r.loanBalance)} pointsB={result.timeline.map((r) => r.equity)} />
                <ChartBand title="Cashflow" leftLabel="Vor Steuern" rightLabel="Nach Steuern" pointsA={result.timeline.map((r) => r.cashflowBeforeTaxMonthly)} pointsB={result.timeline.map((r) => r.cashflowAfterTaxMonthly)} signed />
              </section>
            )}

            {activeTab === "config" && (
              <section className="border border-border p-5">
                <div className="overline mb-3">Konfiguration</div>
                <div className="grid md:grid-cols-2 gap-6">
                  <SummaryTable
                    title="Schwellwerte"
                    rows={[
                      ["Bruttorendite grün", "6.0%"],
                      ["Bruttorendite gelb", "5.5%"],
                      ["Bruttorendite rot", "5.0%"],
                      ["Cashflow grün", ">= €100"],
                    ]}
                  />
                  <SummaryTable
                    title="Profil"
                    rows={[
                      ["Land", form.country],
                      ["Bundesland", form.state],
                      ["GrESt", `${Math.round((result.inputs.transferTaxPct || 0) * 1000) / 10}%`],
                      ["AfA-Satz", `${Math.round((result.inputs.depreciationRatePct || 0) * 1000) / 10}%`],
                    ]}
                  />
                </div>
              </section>
            )}

            {activeTab === "household" && (
              <section className="border border-border p-5">
                <div className="overline mb-3">Haushaltsrechnung</div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Grid2>
                      <Field label="Netto-Einkommen / Monat">
                        <Input type="number" value={household.netIncomeMonthly} onChange={(e) => setNumField(setHousehold, "netIncomeMonthly", e.target.value)} />
                      </Field>
                      <Field label="Sonstiges Einkommen">
                        <Input type="number" value={household.otherIncomeMonthly} onChange={(e) => setNumField(setHousehold, "otherIncomeMonthly", e.target.value)} />
                      </Field>
                      <Field label="Fixkosten">
                        <Input type="number" value={household.fixedCostsMonthly} onChange={(e) => setNumField(setHousehold, "fixedCostsMonthly", e.target.value)} />
                      </Field>
                      <Field label="Bestehende Schuldendienste">
                        <Input type="number" value={household.existingDebtServiceMonthly} onChange={(e) => setNumField(setHousehold, "existingDebtServiceMonthly", e.target.value)} />
                      </Field>
                    </Grid2>
                  </div>
                  <SummaryTable
                    title="Freier Spielraum"
                    rows={[
                      ["Vor neuem Darlehen", fmtMoney(householdBeforeNewDebt)],
                      ["Neuer Schuldendienst", fmtMoney(result.mortgageMonthly)],
                      ["Nach neuem Darlehen", fmtMoney(householdAfterNewDebt)],
                    ]}
                  />
                </div>
              </section>
            )}

            {activeTab === "assets" && (
              <section className="border border-border p-5">
                <div className="overline mb-3">Vermögensaufstellung</div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Grid2>
                      <Field label="Bargeld">
                        <Input type="number" value={assets.cash} onChange={(e) => setNumField(setAssets, "cash", e.target.value)} />
                      </Field>
                      <Field label="Wertpapiere">
                        <Input type="number" value={assets.securities} onChange={(e) => setNumField(setAssets, "securities", e.target.value)} />
                      </Field>
                      <Field label="Sonstiges Vermögen">
                        <Input type="number" value={assets.otherAssets} onChange={(e) => setNumField(setAssets, "otherAssets", e.target.value)} />
                      </Field>
                      <Field label="Bestehende Schulden">
                        <Input type="number" value={assets.existingDebt} onChange={(e) => setNumField(setAssets, "existingDebt", e.target.value)} />
                      </Field>
                    </Grid2>
                  </div>
                  <SummaryTable
                    title="Nettovermögen"
                    rows={[
                      ["Liquid", fmtMoney(assets.cash + assets.securities)],
                      ["Gesamtvermögen", fmtMoney(netWorth)],
                      ["Objektanteil", fmtMoney(result.equityRequired)],
                    ]}
                  />
                </div>
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function loadStoredForm() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return { ...getObjectCockpitDefaults(), ...stored };
  } catch {
    return getObjectCockpitDefaults();
  }
}

function numField(value) {
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

function setNumField(setter, key, raw) {
  const value = numField(raw);
  setter((prev) => ({ ...prev, [key]: value }));
}

function fmtMoney(value) {
  const n = Number(value || 0);
  return `€${n.toLocaleString("de-AT", { maximumFractionDigits: 0 })}`;
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className="text-signal-green" />
      <div className="overline">{title}</div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest2 text-text-muted mb-1">{label}</div>
      {children}
      {hint && <div className="mt-1 text-[10px] font-mono text-text-muted">{hint}</div>}
    </label>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function PercentInput({ value, onChange }) {
  return (
    <Input
      type="number"
      step="0.001"
      value={value}
      onChange={(e) => onChange(Number.isFinite(Number(e.target.value)) ? Number(e.target.value) : 0)}
    />
  );
}

function SummaryTable({ title, rows }) {
  return (
    <div className="border border-border">
      <div className="border-b border-border px-3 py-2 overline text-text-muted">{title}</div>
      <table className="w-full text-[11px] font-mono">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label} className="border-b border-border/50 last:border-b-0">
              <td className="px-3 py-2 text-text-muted">{label}</td>
              <td className="px-3 py-2 text-right text-white">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTable({ rows }) {
  return (
    <div className="overflow-x-auto border border-border">
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="bg-surface border-b border-border">
            <th className="px-3 py-2 text-left">Jahr</th>
            <th className="px-3 py-2 text-left">Miete p.a.</th>
            <th className="px-3 py-2 text-left">Wert</th>
            <th className="px-3 py-2 text-left">Restschuld</th>
            <th className="px-3 py-2 text-left">Eigenkapital</th>
            <th className="px-3 py-2 text-left">CF nach St.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.yearOffset} className="border-b border-border/50 hover:bg-surface/50">
              <td className="px-3 py-2 text-text-muted">{row.year}</td>
              <td className="px-3 py-2 text-white">{fmtMoney(row.annualNetRent)}</td>
              <td className="px-3 py-2 text-white">{fmtMoney(row.propertyValue)}</td>
              <td className="px-3 py-2 text-white">{fmtMoney(row.loanBalance)}</td>
              <td className="px-3 py-2 text-white">{fmtMoney(row.equity)}</td>
              <td className={`px-3 py-2 ${row.cashflowAfterTaxMonthly >= 0 ? "text-signal-green" : "text-signal-red"}`}>{fmtMoney(row.cashflowAfterTaxMonthly)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartBand({ title, leftLabel, rightLabel, pointsA, pointsB, signed = false }) {
  return (
    <section className="border border-border p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="overline">{title}</div>
        <div className="text-[10px] font-mono text-text-muted">{leftLabel} · {rightLabel}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <MiniChart label={leftLabel} values={pointsA} signed={signed} />
        <MiniChart label={rightLabel} values={pointsB} signed={signed} />
      </div>
    </section>
  );
}

function MiniChart({ label, values, signed = false }) {
  const width = 420;
  const height = 180;
  const max = Math.max(...values, 1);
  const min = signed ? Math.min(...values, 0) : 0;
  const span = Math.max(max - min, 1);
  const pts = values.map((value, idx) => {
    const x = values.length <= 1 ? 0 : (idx / (values.length - 1)) * width;
    const y = height - (((value - min) / span) * (height - 20)) - 10;
    return `${x},${y}`;
  }).join(" ");
  return (
    <div className="border border-border bg-bg p-3">
      <div className="text-[10px] font-mono text-text-muted mb-2">{label}</div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40">
        <line x1="0" y1={height - 12} x2={width} y2={height - 12} stroke="rgba(255,255,255,0.08)" />
        <polyline points={pts} fill="none" stroke="#00ff66" strokeWidth="2" />
        {values.map((value, idx) => {
          const x = values.length <= 1 ? 0 : (idx / (values.length - 1)) * width;
          const y = height - (((value - min) / span) * (height - 20)) - 10;
          return <circle key={idx} cx={x} cy={y} r="2.5" fill="#00ff66" />;
        })}
      </svg>
    </div>
  );
}
